import React, { useEffect, useMemo, useState } from 'react';
import { createAvatar } from '@dicebear/core';
import * as funEmoji from '@dicebear/fun-emoji';
import API from '../api/API';
import { User } from '../config/Registration';
import './UserAvatar.css';

interface UserAvatarProps {
  user: User;
  size?: 'header' | 'large';
  onClick?: () => void;
  className?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ user, size = 'header', onClick, className = '' }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    if (!user?.avatarUrl) {
      setImageUrl(null);
      return;
    }

    API.getBlob(user.avatarUrl, { t: new Date().getTime() })
      .then((blob) => {
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      })
      .catch(() => setImageUrl(null));

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [user?.avatarUrl]);

  const fallbackAvatarUrl = useMemo(() => {
    const seed = String(user?.id || user?.email?.trim() || 'eufooria-user');

    return createAvatar(funEmoji, {
      seed,
      backgroundColor: ['f2d3b1', 'e5f4fb', 'd1d4f9', 'ffd5dc', 'c0aede'],
    }).toDataUri();
  }, [user?.id, user?.email]);

  const content = imageUrl ? (
    <img src={imageUrl} alt="Profile" />
  ) : (
    <img src={fallbackAvatarUrl} alt="Default profile avatar" />
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`user-avatar user-avatar-${size} ${className}`}
        onClick={onClick}
        aria-label="Open profile"
      >
        {content}
      </button>
    );
  }

  return (
    <div className={`user-avatar user-avatar-${size} ${className}`}>
      {content}
    </div>
  );
};

export default UserAvatar;
